import * as TabsPrimitive from '@radix-ui/react-tabs';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FC, ReactElement, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, Outlet } from 'react-router-dom';
import { cn, FaIcon, Skeleton } from '@fe-learner-common';
import { useBreakpoint } from '@fe-learner-common/hooks/useBreakpoint';

export type Tab = {
  titleId: string;
  value: string;
  icon: IconDefinition;
  reloadOnClick?: boolean;
};

type TabContent = {
  triggers: ReactElement[];
  content: ReactElement[];
};

type TabsProps = {
  tabs: Tab[];
  isLoading?: boolean;
  selectedTab?: string;
  onValueChange?: (value: string) => void;
};

const TriggerSkeleton: FC = () => {
  return (
    <>
      <Skeleton type="button" className="w-full" />
      <Skeleton type="button" className="w-full" />
      <Skeleton type="button" className="w-full" />
      <Skeleton type="button" className="w-full" />
    </>
  );
};

/*
 * A component that renders a tabbed view with a list of tabs and their content.
 *
 * @state - index - The only use is to update the key of the triggers and content, to support the functionality of re-rendering a tab when if the trigger is clicked when already active.
 * */
export const Tabs: FC<TabsProps> = ({
  selectedTab,
  tabs,
  isLoading,
  onValueChange,
}) => {
  const [index, setIndex] = useState(0);
  const selectedRef = useRef<string | null>(null);
  const intl = useIntl();
  const matchesLg = useBreakpoint('lg');

  const tabContent = useMemo<TabContent | null>(() => {
    if (!tabs.length) return null;

    return tabs.reduce(
      (acc, cur) => {
        if (cur) {
          const trigger = (
            <TabsPrimitive.Trigger
              asChild
              key={`course-info-tab-${cur.value}-trigger-${
                cur?.reloadOnClick ? index : ''
              }`}
              autoFocus={selectedRef.current === cur.value}
              value={cur.value}
              onClick={() => {
                setIndex((prev) => prev + 1);
                selectedRef.current = cur.value;
              }}
              className="flex items-center gap-3 rounded-lg px-4 py-2.5 text-label-lg hover:bg-neutral-100 hover:text-neutral-900 data-[state=active]:bg-neutral-100 data-[state=active]:text-neutral-900"
            >
              <Link to={cur.value}>
                <FaIcon width={20} icon={cur.icon} />
                <span>{intl.formatMessage({ id: cur.titleId })}</span>
              </Link>
            </TabsPrimitive.Trigger>
          );

          const content = (
            <TabsPrimitive.Content
              value={cur.value}
              asChild
              key={`course-info-tab-${cur.value}-content-${
                cur?.reloadOnClick ? index : ''
              }`}
            >
              <section className="flex flex-1 flex-col gap-y-6 px-3 pb-6 md:gap-y-4 md:pb-0 lg:px-0">
                <Outlet />
              </section>
            </TabsPrimitive.Content>
          );

          return {
            triggers: [...acc.triggers, trigger],
            content: [...acc.content, content],
          };
        }

        return acc;
      },
      { triggers: [], content: [] },
    );
  }, [tabs, index, intl]);

  if (!tabContent) return null;
  return (
    <div className="lg:l-page flex-1 2xl:container">
      <TabsPrimitive.Root
        orientation={matchesLg ? 'vertical' : 'horizontal'}
        className="flex flex-col gap-8 lg:flex-row lg:gap-x-16"
        activationMode="manual"
        value={selectedTab}
        onValueChange={(value) => {
          onValueChange?.(value);
        }}
      >
        <TabsPrimitive.List
          aria-label="Course information"
          className={cn(
            'flex w-full flex-row gap-3 overflow-x-auto px-6 py-4 text-neutral-500',
            'lg:mx-3 lg:w-[320px] lg:min-w-[320px] lg:flex-col lg:gap-2 lg:overflow-visible lg:p-0',
          )}
        >
          {isLoading ? <TriggerSkeleton /> : tabContent.triggers}
        </TabsPrimitive.List>

        {tabContent.content}
      </TabsPrimitive.Root>
    </div>
  );
};
